import { useDispatch, useSelector } from 'react-redux';
import { CareSettingsSlideOutContent, SlideOutSheet, OnboardingChrome, Badge } from '../components';
import { putCareEnvironmentSelectionsForClientByClientIdRequest, selectClient } from '../model';
import { CarePhaseCostSummary } from '../components/CarePhaseCostSummary';
import { formatCurrency } from '@shared/formatCurrency';
import { SlideContent } from '../components/SlideContent';
import { FunctionComponent, useState } from 'react';
import {
  careEnvironmentsApplicableToPhase,
  carePhaseDefs,
  CareEnvironment,
  CarePhase,
  pickClientCareEnvironmentCosts,
} from '@shared';
import {
  OnboardingFamilyCaregivingPhaseOne,
  OnboardingFamilyCaregivingPhaseTwo,
  OnboardingFamilyCaregivingPhaseThree,
  OnboardingCarePhasesSummary,
  OnboardingCarePhasesPhaseTwo,
  OnboardingCarePhasesPhaseThree,
} from '../pages';
import { ClockIcon } from '@heroicons/react/24/outline';
import { CalendarDaysIcon } from '@heroicons/react/24/outline';
import { BsBuildingAdd } from 'react-icons/bs';
import { cn, CustomizePlanButtonGroup, isInferenceEdited } from '..';
import { PiBuildingLight, PiHospitalLight, PiWarehouseLight } from 'react-icons/pi';

function determineNextSlide(isCareSettingHome: boolean, carePhase: CarePhase) {
  if (isCareSettingHome) {
    switch (carePhaseDefs[carePhase].index) {
      case 0:
        return OnboardingFamilyCaregivingPhaseOne;
      case 1:
        return OnboardingFamilyCaregivingPhaseTwo;
      case 2:
        return OnboardingFamilyCaregivingPhaseThree;
      default:
        return undefined;
    }
  }

  switch (carePhaseDefs[carePhase].index) {
    case 0:
      return OnboardingCarePhasesPhaseTwo;
    case 1:
      return OnboardingCarePhasesPhaseThree;
    case 2:
      return OnboardingCarePhasesSummary;
    default:
      return undefined;
  }
}

function determineNextSlideLabel(
  isCareSettingHome: boolean,
  carePhase: CarePhase,
  clientFirstName: string,
) {
  if (isCareSettingHome) {
    return `Next`;
  }

  return carePhaseDefs[carePhase].index === 2 // check if last phase
    ? `Review ${clientFirstName}'s Costs`
    : `Go to ${carePhaseDefs[carePhase + 1].label}`;
}

interface CarePhaseProps {
  carePhase: CarePhase;
  slidePage: FunctionComponent<ClientIdProps>;
}

function OnboardingCareSettings({ carePhase, slidePage }: CarePhaseProps) {
  const client = useSelector(selectClient)!;
  const {
    clientFirstName,
    intakeSurvey: { clientBirthYear, clientCurrentCarePhase, clientPhaseStartAges },
    phaseCosts,
    clientPhasePredictedStartYears,
    clientPhaseInflationFactors,
  } = client;

  const dispatch = useDispatch();
  const [selectedCareSetting, setSelectedCareSetting] = useState<number | null>(
    client.careEnvironmentSelections?.[carePhase] ?? null,
  );
  const carePhaseIndex = carePhaseDefs[carePhase].index;
  const selectedCarePhaseCosts = phaseCosts[carePhaseIndex];
  const predictedPhaseStartYear = clientPhasePredictedStartYears[carePhase];
  const phaseAge = predictedPhaseStartYear
    ? predictedPhaseStartYear - clientBirthYear
    : clientPhaseStartAges[carePhase]!;
  const carePhaseDurationMonths = selectedCarePhaseCosts.phaseCareMonthsNeeded;
  const applicableCareSettings = careEnvironmentsApplicableToPhase[carePhase];
  const carePhaseStartAge = clientPhaseStartAges[carePhase];
  const isDurationCustom = selectedCarePhaseCosts.isDurationCustom;

  const handleButtonClick = (careSettingDbId: number) => {
    if (careSettingDbId === selectedCareSetting) {
      setSelectedCareSetting(null);

      dispatch(
        putCareEnvironmentSelectionsForClientByClientIdRequest({
          clientId: client.clientId,
          careEnvironmentSelections: {
            ...(client.careEnvironmentSelections ?? {}),
            [carePhase]: null,
          },
        }),
      );
      return;
    }

    setSelectedCareSetting(careSettingDbId);
    dispatch(
      putCareEnvironmentSelectionsForClientByClientIdRequest({
        clientId: client.clientId,
        careEnvironmentSelections: {
          ...(client.careEnvironmentSelections ?? {}),
          [carePhase]: careSettingDbId,
        },
      }),
    );
  };

  const isCareSettingHome = selectedCareSetting === CareEnvironment.home;

  return (
    <>
      <OnboardingChrome
        slidePage={slidePage}
        nextButtonLabel={determineNextSlideLabel(isCareSettingHome, carePhase, clientFirstName)}
        nextSlidePage={determineNextSlide(isCareSettingHome, carePhase)}
        disableNextButton={selectedCareSetting === null}
        errorModalMessage="Oops! You need to select a care setting to continue."
      >
        <SlideContent
          headerText={
            <span data-testid="care-settings-header">{carePhaseDefs[carePhase].label}</span>
          }
          headerSubText={
            <>
              <div className="bg-primary-50 flex flex-col gap-x-4 gap-y-3 rounded-lg p-3 text-base md:inline-flex md:flex-row">
                <div className="flex items-center gap-1">
                  <CalendarDaysIcon className=" h-4 w-4 md:block md:h-5 md:w-5" />
                  <div>
                    {carePhase === clientCurrentCarePhase ? (
                      <>
                        Began at age{' '}
                        <span className="text-primary font-semibold">
                          {Math.round(carePhaseStartAge!)}
                        </span>
                      </>
                    ) : (
                      <>
                        Estimated begin:{' '}
                        <span className="text-primary font-semibold">
                          {Math.round(phaseAge)} years old
                        </span>
                      </>
                    )}
                    {isInferenceEdited(client, 'ltcAtAge') && (
                      <Badge
                        color="gray"
                        label="edited"
                        className="ml-2"
                      />
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <ClockIcon className=" h-4 w-4 md:block md:h-5 md:w-5" />
                  <div>
                    <>
                      Estimated duration:{' '}
                      <span className="text-primary font-semibold">
                        {carePhaseDurationMonths}{' '}
                        {carePhase === clientCurrentCarePhase ? 'more ' : ''} months
                      </span>
                    </>
                    {isDurationCustom && (
                      <Badge
                        color="gray"
                        label="edited"
                        className="ml-2"
                      />
                    )}
                  </div>
                </div>
              </div>
            </>
          }
        >
          <CustomizePlanButtonGroup
            client={client}
            className="my-5"
          />
          <div className="bg-primary-50 flex flex-col gap-4 gap-x-8 rounded-lg p-4 md:mt-8 md:grid-cols-2  md:flex-row md:p-6 lg:mb-0">
            <div className="flex flex-col md:w-[55%] md:max-w-[70%]">
              <h2 className="mb-4 text-base font-normal leading-6 text-gray-800 md:mb-0 md:text-xl">
                Select
                {carePhase === clientCurrentCarePhase
                  ? ` where ${clientFirstName} is living during ${carePhaseDefs[carePhase].label}.`
                  : ` where ${clientFirstName} will live during ${carePhaseDefs[carePhase].label}.`}
              </h2>

              <div className="flex flex-col md:mt-8">
                {/* care setting buttons */}
                <div className="flex flex-wrap justify-between gap-4 md:gap-x-8">
                  {applicableCareSettings.map(careSettingData => (
                    <button
                      key={careSettingData.dbId}
                      className={cn(
                        `relative col-span-1 flex h-36 min-w-[215px] flex-1 flex-col items-center justify-center rounded-md border px-1 py-2 pb-3 text-center hover:border-2 md:px-4 md:py-0 [@media_(min-width:_1047px)]:h-48`,
                        `${
                          selectedCareSetting === careSettingData.dbId
                            ? 'border-green-600 bg-green-50'
                            : '  border-primary-300 bg-white hover:bg-gray-50'
                        } `,
                      )}
                      data-testid={(() => {
                        switch (careSettingData.dbId) {
                          case CareEnvironment.home:
                            return 'care-settings-option-home';
                          case CareEnvironment.independentLiving:
                            return 'care-settings-option-independent';
                          case CareEnvironment.assistedLiving:
                            return 'care-settings-option-assisted';
                          case CareEnvironment.fullCareFacility:
                            return 'care-settings-option-fullcare';
                          default:
                            return undefined as unknown as string;
                        }
                      })()}
                      onClick={() => handleButtonClick(careSettingData.dbId)}
                    >
                      {/* radio button circle */}
                      <div
                        className={`absolute left-1 top-1 h-5 w-5 rounded-full border md:left-4 md:top-4 ${
                          selectedCareSetting === careSettingData.dbId
                            ? 'border-green-600'
                            : 'border-gray-400'
                        }`}
                      >
                        {selectedCareSetting === careSettingData.dbId && (
                          <div className="absolute left-1/2 top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-green-600" />
                        )}
                      </div>

                      {/* predicted badge */}
                      {careSettingData.dbId ===
                        selectedCarePhaseCosts.recommendedCareEnvironment && (
                        <Badge
                          color="primary"
                          label="Predicted Preference"
                          className="absolute right-1 top-1 md:right-3 md:top-3"
                        />
                      )}

                      {/* icon and label */}
                      <div className="mt-8 flex flex-col items-center">
                        <div className="flex items-center">
                          {(() => {
                            switch (careSettingData.dbId) {
                              case CareEnvironment.home:
                                return <PiWarehouseLight className="text-2xl md:text-4xl" />;
                              case CareEnvironment.independentLiving:
                                return <PiBuildingLight className="text-2xl md:text-4xl" />;
                              case CareEnvironment.assistedLiving:
                                return <BsBuildingAdd className="text-2xl md:h-8 md:w-8" />;
                              case CareEnvironment.fullCareFacility:
                                return <PiHospitalLight className="text-2xl md:text-4xl" />;
                            }
                          })()}
                        </div>
                        <div className="mt-2 text-sm font-semibold text-gray-800 md:text-lg">
                          {careSettingData.label}
                        </div>
                      </div>

                      {/* cost */}
                      <div className="flex justify-end md:mt-1">
                        <span className="inline-flex items-center text-sm font-normal leading-8 text-gray-800 md:text-base">
                          {careSettingData.dbId === CareEnvironment.home
                            ? '$0 - ' +
                              formatCurrency(
                                calcInflatedMaximumFamilyShareCostFor(
                                  selectedCarePhaseCosts,
                                  client,
                                  clientPhaseInflationFactors[carePhase]!,
                                ),
                              )
                            : formatCurrency(
                                calcInflatedProfessionalShareCostFor(
                                  selectedCarePhaseCosts,
                                  client,
                                  clientPhaseInflationFactors[carePhase]!,
                                  careSettingData.dbId as CareEnvironment,
                                ),
                              )}
                        </span>
                      </div>
                    </button>
                  ))}
                </div>
                <SlideOutSheet
                  slideOutContent={<CareSettingsSlideOutContent />}
                  title="FAQ"
                >
                  <div className="text-primary hover:text-primary-hover ml-1 mt-4 text-left text-sm underline md:text-base">
                    What are care settings?
                  </div>
                </SlideOutSheet>
              </div>
            </div>
            <CarePhaseCostSummary
              client={client}
              carePhase={carePhase}
            />
          </div>
        </SlideContent>
      </OnboardingChrome>
    </>
  );
}

export function OnboardingCareSettingsPhaseOne({ clientId }: ClientIdProps) {
  return (
    <OnboardingCareSettings
      carePhase={carePhaseDefs.earlyCare.value}
      slidePage={OnboardingCareSettingsPhaseOne}
    />
  );
}

export function OnboardingCareSettingsPhaseTwo({ clientId }: ClientIdProps) {
  return (
    <OnboardingCareSettings
      carePhase={carePhaseDefs.moderateCare.value}
      slidePage={OnboardingCareSettingsPhaseTwo}
    />
  );
}

export function OnboardingCareSettingsPhaseThree({ clientId }: ClientIdProps) {
  return (
    <OnboardingCareSettings
      carePhase={carePhaseDefs.fullCare.value}
      slidePage={OnboardingCareSettingsPhaseThree}
    />
  );
}

function calcInflatedMaximumFamilyShareCostFor(
  phaseCosts: SinglePhaseCosts,
  client: Client,
  phaseInflationFactor: number,
) {
  const careEnvironmentCostData = pickClientCareEnvironmentCosts(client, CareEnvironment.home);
  return (
    careEnvironmentCostData.rateAmount * phaseCosts.phaseCareHoursNeeded * phaseInflationFactor
  );
}

function calcInflatedProfessionalShareCostFor(
  phaseCosts: SinglePhaseCosts,
  client: Client,
  phaseInflationFactor: number,
  careEnvironment: CareEnvironment,
) {
  const careEnvironmentCostData = pickClientCareEnvironmentCosts(client, careEnvironment);
  return (
    careEnvironmentCostData.rateAmount * phaseCosts.phaseCareMonthsNeeded * phaseInflationFactor
  );
}
